import { useAuthStore } from '@/store/auth';
import { useQuery } from '@tanstack/react-query';
import { isChargerLive, type SiteComparisonRow } from '@evi/shared';
import { fetchChargers } from '@/lib/api';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { SiteComparison } from './SiteComparison';
import styles from './SitePages.module.css';
import { LocationBankCard } from './LocationBankCard';
import { useBankReadiness } from '@/components/site/BankSetupBanner';

function LocationBanks() {
  const query = useBankReadiness();
  if (query.isPending) return <p>Checking your locations...</p>;
  if (query.isError) return <p role="alert">Could not check location banks. <button onClick={() => void query.refetch()}>Retry</button></p>;
  return <>{query.data.locations.map(site => <section key={site.id} className={styles.card}>
    <h2 className={styles.cardTitle}>{site.name}</h2><LocationBankCard locationId={site.id} compact />
  </section>)}{query.data.locations.length === 0 && <p>No assigned location currently requires electricity reimbursement setup.</p>}</>;
}

function ChargerLists({ rows }: { rows: SiteComparisonRow[] }) {
  const userId = useAuthStore((state) => state.user?._id);
  const query = useQuery({ queryKey: ['my-site', 'all-scoped-chargers', userId], queryFn: async () => {
    const first = await fetchChargers({ limit: 100, page: 1 });
    const data = [...first.data];
    for (let page = 2; page <= first.meta.pages; page++) data.push(...(await fetchChargers({ limit: 100, page })).data);
    return data;
  } });
  if (query.isPending) return <p className={styles.muted}>Loading chargers…</p>;
  if (query.isError) return <p role="alert" className={styles.error}>Could not load chargers.</p>;
  // Online = the Dashboard KPI's rule (flag AND a heartbeat within 15 min),
  // not the raw flag, which stays true after a socket dies without a close.
  const now = Date.now();
  return <>{rows.map((site) => <section key={site.location_id} className={styles.card}>
    <h2 className={styles.cardTitle}>{site.name}</h2>
    <LocationBankCard locationId={site.location_id} compact />
    <ul className={styles.list}>{query.data.filter((c) => String(c.location_id) === site.location_id && c.is_active !== false).map((c) => {
      const live = isChargerLive(c, now);
      return <li key={c._id} className={styles.listRow}>
        <span className={styles.rowName}>{c.display_name ?? c.identity}</span><span className={live ? styles.badgeOk : styles.badgeOff}>{live ? 'Online' : 'Offline'}</span>
      </li>;
    })}</ul>
    {site.charger_count === 0 && <p className={styles.muted}>No active chargers at this location.</p>}
  </section>)}</>;
}

export function MySitePage() {
  useRecentRoute('My Sites');
  return <div className={styles.page}><header className={styles.header}><h1 className={styles.title}>My Sites</h1><a href="/site-settings">Manage electricity rates and site settings</a></header>
    {new URLSearchParams(window.location.search).get('banks') === '1' ? <LocationBanks /> : <SiteComparison>{(rows) => <ChargerLists rows={rows} />}</SiteComparison>}
  </div>;
}
export default MySitePage;
